import React, { useState } from 'react';
import { Upload, Image as ImageIcon, Trash2, Plus } from 'lucide-react';
import { HeroContent } from '../ContentBuilder';
import { EditableText } from '../components/EditableText';
import { ImageDropZone } from '../components/ImageDropZone';
import { MediaDropZone } from '../components/MediaDropZone';
import { BuilderButton } from '../components/BuilderButton';

export interface PendingImages {
  backgroundFile?: File;
  backgroundPreview?: string;
  backgroundMediaType?: 'image' | 'video';
  logoFile?: File;
  logoPreview?: string;
}

interface HeroSectionProps {
  content: HeroContent;
  onUpdate: (updates: Partial<HeroContent>) => void;
  organizationId: string;
  isPreview: boolean;
  onPendingImagesChange?: (images: PendingImages) => void;
  accentColor?: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  onPendingImagesChange,
  accentColor,
}) => {
  const [pendingImages, setPendingImages] = useState<PendingImages>({});

  const handleBackgroundMediaUpload = (file: File, type: 'image' | 'video') => {
    const previewUrl = URL.createObjectURL(file);
    const newPending = {
      ...pendingImages,
      backgroundFile: file,
      backgroundPreview: previewUrl,
      backgroundMediaType: type,
    };
    setPendingImages(newPending);
    if (onPendingImagesChange) {
      onPendingImagesChange(newPending);
    }
  };

  const handleLogoUpload = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    const newPending = {
      ...pendingImages,
      logoFile: file,
      logoPreview: previewUrl,
    };
    setPendingImages(newPending);
    if (onPendingImagesChange) {
      onPendingImagesChange(newPending);
    }
  };

  const handleStatUpdate = (index: number, field: 'value' | 'label', value: string) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    onUpdate({ stats: newStats });
  };

  const addStat = () => {
    const newStat = { value: '[Value]', label: '[Label]' };
    onUpdate({ stats: [...content.stats, newStat] });
  };

  const removeStat = (index: number) => {
    const updatedStats = content.stats.filter((_, i) => i !== index);
    onUpdate({ stats: updatedStats });
  };

  // Determine if we have video content
  const isVideoBackground =
    pendingImages.backgroundMediaType === 'video' ||
    (content.backgroundVideo && !pendingImages.backgroundPreview);

  const backgroundMediaUrl =
    pendingImages.backgroundPreview || content.backgroundVideo || content.backgroundImage;

  return (
    <div className="relative w-full min-h-[600px] overflow-hidden">
      {/* Background Image/Video */}
      <div className="absolute inset-0">
        {backgroundMediaUrl ? (
          <div className="relative w-full h-full">
            {isVideoBackground ? (
              <video
                src={backgroundMediaUrl}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={backgroundMediaUrl}
                alt="Hero Background"
                className="w-full h-full object-cover"
              />
            )}
            {!isPreview && (
              <>
                <div className="absolute inset-0">
                  <MediaDropZone
                    onMediaDrop={handleBackgroundMediaUpload}
                    isUploading={false}
                    label="Drop image or video"
                    acceptVideo={true}
                  />
                </div>
                <div className="absolute top-5 right-10 z-30 w-32 h-32 rounded-full overflow-hidden">
                  <MediaDropZone
                    onMediaDrop={handleBackgroundMediaUpload}
                    isUploading={false}
                    label="Drop media"
                    acceptVideo={true}
                    compact
                  />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 relative">
            <div className="absolute top-5 right-10 z-30 w-32 h-32 rounded-full overflow-hidden">
              {!isPreview && (
                <MediaDropZone
                  onMediaDrop={handleBackgroundMediaUpload}
                  isUploading={false}
                  label="Drop media"
                  acceptVideo={true}
                  showAlways
                  compact
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dark overlay for better text visibility */}
      <div className="absolute inset-0 bg-zinc-950 bg-opacity-60" />

      {/* Content */}
      <div className="relative z-20 flex flex-col h-full min-h-[600px]">
        {/* Header/Navigation */}
        <div className="px-8 py-6">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            {/* Logo */}
            <div className="flex-shrink-0">
              {pendingImages.logoPreview || content.logo ? (
                <div className="relative group">
                  <div className="w-32 h-32 flex items-center justify-center">
                    <img
                      src={pendingImages.logoPreview || content.logo}
                      alt="Company Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {!isPreview && (
                    <div className="absolute inset-0">
                      <ImageDropZone
                        onImageDrop={handleLogoUpload}
                        isUploading={false}
                        label="Drop new logo"
                        compact
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-32 h-32 flex items-center justify-center relative">
                  <span className="text-white font-bold text-xl">[LOGO]</span>
                  {!isPreview && (
                    <ImageDropZone
                      onImageDrop={handleLogoUpload}
                      isUploading={false}
                      label="Drop logo"
                      showAlways
                      compact
                    />
                  )}
                </div>
              )}
            </div>

            {/* Navigation - Static */}
            <nav className="hidden md:flex items-center gap-6">
              {['HOME', 'PROPERTY SEARCH', 'BUYING', 'SELLING', 'ABOUT US'].map((label, index) => (
                <button
                  key={index}
                  className="text-white text-sm font-medium hover:text-yellow-400 transition-colors uppercase tracking-wide"
                >
                  {label}
                </button>
              ))}
            </nav>

            {/* Action Button - Static */}
            <div className="flex-shrink-0">
              <BuilderButton accentColor={accentColor}>CONTACT US</BuilderButton>
            </div>
          </div>
        </div>

        {/* Hero Content - Center */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
          <div className="max-w-5xl mx-auto text-center space-y-6">
            {/* Headline */}
            <h1 className="text-white text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
              <EditableText
                value={content.headline}
                onChange={(value: string) => onUpdate({ headline: value })}
                isPreview={isPreview}
                multiline
                className="text-center"
              />
            </h1>

            {/* Tagline */}
            <div className="inline-block px-12 py-4">
              <p className="text-white text-xl md:text-2xl font-semibold uppercase tracking-wide">
                <EditableText
                  value={content.tagline}
                  onChange={(value: string) => onUpdate({ tagline: value })}
                  isPreview={isPreview}
                  className="text-center"
                />
              </p>
            </div>

            {/* CTA Buttons - Editable */}
            <div className="flex items-center justify-center gap-4 pt-6">
              <BuilderButton accentColor={accentColor}>
                <EditableText
                  value={content.primaryCTA}
                  onChange={(value: string) => onUpdate({ primaryCTA: value })}
                  isPreview={isPreview}
                  className="text-center"
                />
              </BuilderButton>
              <BuilderButton accentColor={accentColor}>
                <EditableText
                  value={content.secondaryCTA}
                  onChange={(value: string) => onUpdate({ secondaryCTA: value })}
                  isPreview={isPreview}
                  className="text-center"
                />
              </BuilderButton>
            </div>
          </div>
        </div>

        {/* Stats Section - Bottom */}
        <div className="px-8 pb-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {content.stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/40 backdrop-blur-sm rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all hover:bg-white/30 border border-white/30 relative"
                >
                  {/* Delete Button */}
                  {!isPreview && content.stats.length > 1 && (
                    <button
                      onClick={() => removeStat(index)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors z-10"
                      title="Remove stat"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                  <div className="text-white text-3xl font-bold mb-2">
                    <EditableText
                      value={stat.value}
                      onChange={(value: string) => handleStatUpdate(index, 'value', value)}
                      isPreview={isPreview}
                      className="text-center"
                      iconColor="text-white"
                    />
                  </div>
                  <div className="text-white text-xs font-semibold uppercase tracking-wide">
                    <EditableText
                      value={stat.label}
                      onChange={(value: string) => handleStatUpdate(index, 'label', value)}
                      isPreview={isPreview}
                      multiline
                      className="text-center"
                      iconColor="text-white"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Add Stat Button */}
            {!isPreview && (
              <div className="flex justify-center">
                <button
                  onClick={addStat}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Stat
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
